import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { VoypApiError } from '@/lib/api/client'
import {
  adminApi,
  debugApi,
  type CallFilters,
  type EnterpriseFilters,
  type EventFilters,
  type UserFilters,
} from '@/lib/api/endpoints'
import { useAuth } from '@/lib/firebase/auth'
import type {
  EnterpriseInput,
  MongoQuery,
  Template,
  TemplateCategory,
  UserRole,
} from '@/lib/api/types'
import { queryKeys } from './keys'

/**
 * Admin gate probe.
 *
 * Held until Firebase has restored the persisted session — firing it while
 * `status` is still 'loading' would fail with "Not signed in" and, because
 * retries are disabled, never recover. Retries stay off on purpose: a 403 is a
 * definitive answer, and retrying it only delays the "not authorized" screen.
 */
export function useMe() {
  const { status } = useAuth()
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: adminApi.me,
    enabled: status === 'signedIn',
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUsers(filters: UserFilters) {
  return useQuery({
    queryKey: queryKeys.users(filters),
    queryFn: () => adminApi.listUsers(filters),
    placeholderData: (previous) => previous,
  })
}

export function useUser(uid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.user(uid ?? ''),
    queryFn: () => adminApi.getUser(uid!),
    enabled: Boolean(uid),
  })
}

export function useEnterprises(filters: EnterpriseFilters) {
  return useQuery({
    queryKey: queryKeys.enterprises(filters),
    queryFn: () => adminApi.listEnterprises(filters),
    placeholderData: (previous) => previous,
  })
}

/** Enterprise plus its members, API keys and up to 24 months of usage. */
export function useEnterprise(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.enterprise(id ?? ''),
    queryFn: () => adminApi.getEnterprise(id!),
    enabled: Boolean(id),
  })
}

export function useCalls(filters: CallFilters) {
  return useQuery({
    queryKey: queryKeys.calls(filters),
    queryFn: () => adminApi.listCalls(filters),
    placeholderData: (previous) => previous,
  })
}

export function useCall(callSid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.call(callSid ?? ''),
    queryFn: () => adminApi.getCall(callSid!),
    enabled: Boolean(callSid),
  })
}

/** Live calls change by the second, so this one polls. */
export function useLiveCalls() {
  return useQuery({
    queryKey: queryKeys.liveCalls,
    queryFn: adminApi.liveCalls,
    refetchInterval: 10_000,
  })
}

export function useScheduledCalls(filters: {
  uid?: string
  status?: string
  limit?: number
  skip?: number
}) {
  return useQuery({
    queryKey: queryKeys.scheduledCalls(filters),
    queryFn: () => adminApi.scheduledCalls(filters),
    placeholderData: (previous) => previous,
  })
}

export function useEvents(filters: EventFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.events(filters),
    queryFn: () => adminApi.listEvents(filters),
    placeholderData: (previous) => previous,
    enabled,
  })
}

export function useEventTypes() {
  return useQuery({
    queryKey: queryKeys.eventTypes,
    queryFn: adminApi.eventTypes,
    staleTime: 10 * 60 * 1000,
  })
}

/** New Relic search only runs on an explicit query — it is slow and rate limited. */
export function useLogs(search: string) {
  return useQuery({
    queryKey: queryKeys.logs(search),
    queryFn: () => adminApi.searchLogs(search),
    enabled: search.trim().length > 0,
    staleTime: 60 * 1000,
  })
}

export function useStats(params: { from?: string; to?: string; interval?: string }) {
  return useQuery({
    queryKey: queryKeys.stats(params),
    queryFn: () => adminApi.stats(params),
  })
}

export function useTemplates() {
  return useQuery({ queryKey: queryKeys.templates, queryFn: adminApi.getTemplates })
}

/**
 * Shared mutation wiring: toast on failure, run a caller-supplied success
 * handler, then invalidate. Keeps every mutation below to a few lines.
 */
function useAdminMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  options: {
    success?: string
    invalidate?: () => void
    /** errors the caller renders itself, which would otherwise be shown twice */
    quiet?: (error: Error) => boolean
  } = {},
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (options.success) toast.success(options.success)
      options.invalidate?.()
      void queryClient.invalidateQueries()
    },
    onError: (error: Error) => {
      if (!options.quiet?.(error)) toast.error('Action failed', { description: error.message })
    },
  })
}

export function useSetRole(uid: string) {
  return useAdminMutation((role: UserRole) => adminApi.setRole(uid, role), { success: 'Role updated' })
}

export function useAdjustCredits(uid: string) {
  return useAdminMutation(
    ({ credits, reason }: { credits: number; reason?: string }) =>
      adminApi.adjustCredits(uid, credits, reason),
    { success: 'Credits updated' },
  )
}

export function useDeleteUser() {
  return useAdminMutation((uid: string) => adminApi.deleteUser(uid), { success: 'User deleted' })
}

export function useResetContacts(uid: string) {
  return useAdminMutation(() => adminApi.resetContacts(uid), { success: 'Contacts access reset' })
}

export function useResetCalendar(uid: string) {
  return useAdminMutation(() => adminApi.resetCalendar(uid), { success: 'Calendar access reset' })
}

export function useAddNumber(uid: string) {
  return useAdminMutation(() => adminApi.addNumber(uid), { success: 'Number assigned' })
}

export function useDeleteNumber(uid: string) {
  return useAdminMutation((number: string) => adminApi.deleteNumber(uid, number), {
    success: 'Number released',
  })
}

export function useDeleteDevice(uid: string) {
  return useAdminMutation((deviceId: string) => adminApi.deleteDevice(uid, deviceId), {
    success: 'Device removed',
  })
}

export function useHangupCall() {
  return useAdminMutation((callSid: string) => adminApi.hangupCall(callSid), {
    success: 'Call hung up',
  })
}

/** The owner email has no Voyp account; the owner dialogs offer to make one. */
export function isOwnerNotFound(error: unknown): boolean {
  return error instanceof VoypApiError && error.code === 'user_not_found'
}

export function useCreateEnterprise() {
  return useAdminMutation((input: EnterpriseInput) => adminApi.createEnterprise(input), {
    success: 'Enterprise created',
    quiet: isOwnerNotFound,
  })
}

export function useUpdateEnterprise(id: string) {
  return useAdminMutation((input: EnterpriseInput) => adminApi.updateEnterprise(id, input), {
    success: 'Enterprise updated',
  })
}

export function useSetEnterpriseOwner(id: string) {
  return useAdminMutation(
    ({ email, createOwner }: { email: string; createOwner?: boolean }) =>
      adminApi.setEnterpriseOwner(id, email, createOwner),
    { success: 'Owner changed', quiet: isOwnerNotFound },
  )
}

/**
 * Id lookup runs on submit, so it is a mutation; a miss is reported inline by
 * the caller rather than toasted.
 */
export function useFindEnterpriseCall(id: string) {
  return useMutation({
    mutationFn: (callId: string) => adminApi.findEnterpriseCall(id, callId),
  })
}

/** The toast reports how many keys went with it, since that is the lasting effect. */
export function useDisableEnterprise(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => adminApi.disableEnterprise(id),
    onSuccess: ({ revokedKeys }) => {
      toast.success('Enterprise disabled', {
        description: `${revokedKeys} API key${revokedKeys === 1 ? '' : 's'} revoked`,
      })
      void queryClient.invalidateQueries()
    },
    onError: (error: Error) => toast.error('Action failed', { description: error.message }),
  })
}

export function useSaveTemplate() {
  return useAdminMutation(
    (template: Template) =>
      template._id ? adminApi.updateTemplate(template) : adminApi.addTemplate(template),
    { success: 'Template saved' },
  )
}

export function useDeleteTemplate() {
  return useAdminMutation((id: string) => adminApi.deleteTemplate(id), {
    success: 'Template deleted',
  })
}

export function useSaveCategory() {
  return useAdminMutation(
    ({ category, isNew }: { category: TemplateCategory; isNew: boolean }) =>
      isNew ? adminApi.addCategory(category) : adminApi.updateCategory(category),
    { success: 'Category saved' },
  )
}

export function useDeleteCategory() {
  return useAdminMutation((id: string) => adminApi.deleteCategory(id), {
    success: 'Category deleted',
  })
}

/* ------------------------------------------------------------------ *
 * Debug tools (superadmin only)
 * ------------------------------------------------------------------ */

export function useDebugCollections() {
  return useQuery({
    queryKey: queryKeys.debugCollections,
    queryFn: debugApi.collections,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Mongo find. Only runs when `enabled` — queries are explicit acts here, not
 * something that should fire while someone is still typing a filter.
 */
export function useDebugFind(query: MongoQuery | null) {
  return useQuery({
    queryKey: queryKeys.debugFind(query ?? ({ collection: '' } as MongoQuery)),
    queryFn: () => debugApi.find(query!),
    enabled: Boolean(query?.collection),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
}

export function useDebugUpdate() {
  return useAdminMutation(
    ({ collection, id, set }: { collection: string; id: unknown; set: string }) =>
      debugApi.update(collection, id, set),
    { success: 'Document updated' },
  )
}

export function useTtsProviders() {
  return useQuery({
    queryKey: queryKeys.ttsProviders,
    queryFn: debugApi.ttsProviders,
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Synthesis is a billed side effect, so it is a mutation rather than a query —
 * it must only ever run when someone presses the button.
 */
export function useTtsSay() {
  return useMutation({
    mutationFn: debugApi.ttsSay,
    onError: (error: Error) => toast.error('Synthesis failed', { description: error.message }),
  })
}

export function useLlmProviders() {
  return useQuery({
    queryKey: queryKeys.llmProviders,
    queryFn: debugApi.llmProviders,
    staleTime: 10 * 60 * 1000,
  })
}

/** A completion is a billed side effect, so it only ever runs on demand. */
export function useLlmComplete() {
  return useMutation({
    mutationFn: debugApi.llmComplete,
    onError: (error: Error) => toast.error('Completion failed', { description: error.message }),
  })
}

/** Placing a call dials a real phone and bills the admin — always on demand. */
export function useCreateTestCall() {
  return useMutation({
    mutationFn: debugApi.createCall,
    onError: (error: Error) => toast.error('Could not start the call', { description: error.message }),
  })
}

export function usePatchTestCall() {
  return useMutation({
    mutationFn: ({ callSid, patch }: { callSid: string; patch: { autoReply?: boolean } }) =>
      debugApi.patchCall(callSid, patch),
    onError: (error: Error) => toast.error('Could not update the call', { description: error.message }),
  })
}

/** Translation is billed per character, so it only runs on demand. */
export function useTranslate() {
  return useMutation({
    mutationFn: debugApi.translate,
    onError: (error: Error) => toast.error('Translation failed', { description: error.message }),
  })
}

export function useCacheScan(pattern: string, cursor: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.cacheScan(pattern, cursor),
    queryFn: () => debugApi.scanCache(pattern, cursor),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
}

export function useCacheValue(key: string | null) {
  return useQuery({
    queryKey: queryKeys.cacheValue(key ?? ''),
    queryFn: () => debugApi.cacheValue(key!),
    enabled: Boolean(key),
    retry: false,
  })
}

export function useDeleteCacheKeys() {
  return useAdminMutation(
    (body: { keys?: string[]; pattern?: string; includeProtected?: boolean }) =>
      debugApi.deleteKeys(body),
    { success: 'Keys deleted' },
  )
}

export function useFlushCache() {
  return useAdminMutation((includeProtected: boolean) => debugApi.flushCache(includeProtected), {
    success: 'Cache flushed',
  })
}
